/**
 * LogtoStreamingProvider
 *
 * Provides Logto's token functions to child components via context.
 * This solves the problem of needing to pass token functions to useStreaming
 * without conditionally calling hooks.
 *
 * For multi-tenant apps, uses getOrganizationToken when an organization is
 * available (determined by client hint from domain). This ensures the JWT
 * contains organization_id claim required for tenant mapping.
 *
 * Usage:
 * 1. Wrap your app with LogtoStreamingProvider (only when Logto is enabled)
 * 2. Use useLogtoStreaming() in components that need getAccessToken
 */

import {
  createContext,
  memo,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLogto } from "@logto/react";
import { useAuthStore } from "@/stores/authStore";
import {
  setLogtoTokenProvider,
  clearLogtoTokenProvider,
} from "@/services/auth/logtoTokenProvider";

// Module-level guard — survives unmount/remount, prevents infinite re-render loop
let _orgVerified = false;

/**
 * Domain-to-Organization mapping for client hint
 * When user accesses from a specific domain, use the corresponding organization
 */
const DOMAIN_ORG_MAP: Record<string, string> = {
  // Stage domains
  "play.lexe.pro": "9gur0a2kpdpw", // IT Consulting Test
  "stage-chat.lexe.pro": "lexe-default-stage", // LEXE Stage Organization
  "lexe-mb.lexe.pro": "qwvi4dz9yjhu", // Studio Legale MB Test
  // Production domain
  "ai.lexe.pro": "vij1h45hy26m", // LEXE Default (PROD)
  // Localhost for development
  localhost: "lexe-default-stage", // Default to LEXE Stage for local dev
};

/**
 * Get organization ID based on current hostname
 */
function getOrganizationFromDomain(): string | undefined {
  const hostname = window.location.hostname;
  return DOMAIN_ORG_MAP[hostname];
}

/**
 * Context type.
 * NOTE: Do NOT expose transient SDK states (isLoading) here — they cause
 * fan-out re-renders across all consumers on every getAccessToken() call
 * because @logto/react v3 toggles isLoading internally.
 */
interface LogtoStreamingContextType {
  getAccessToken: (resource?: string) => Promise<string | undefined>;
  signOut: (redirectUri?: string) => Promise<void>;
  isAuthenticated: boolean;
  currentOrganizationId: string | undefined;
}

/**
 * Context with default stub values
 */
const LogtoStreamingContext = createContext<LogtoStreamingContextType>({
  getAccessToken: async () => undefined,
  signOut: async () => {},
  isAuthenticated: false,
  currentOrganizationId: undefined,
});

/**
 * Provider component - must be used inside LogtoProvider
 */
export function LogtoStreamingProvider({ children }: { children: ReactNode }) {
  const logto = useLogto();
  const [currentOrganizationId, setCurrentOrganizationId] = useState<
    string | undefined
  >(getOrganizationFromDomain());

  // Keep a ref to logto so callbacks don't depend on unstable hook references.
  // useLogto() returns new function refs on every render in @logto/react v3,
  // which would cause useCallback/useMemo to recompute → infinite re-renders.
  const logtoRef = useRef(logto);
  logtoRef.current = logto;

  // Extract primitive to avoid unstable object ref in deps
  const isAuthenticated = logto.isAuthenticated;

  // On auth, verify user is in the expected organization and sync user info to authStore
  useEffect(() => {
    if (!isAuthenticated || _orgVerified) return;
    _orgVerified = true;

    const verifyOrganizationAndSyncUser = async () => {
      try {
        const claims = await logtoRef.current.getIdTokenClaims();
        const userOrgs = claims?.organizations ?? [];
        const domainOrg = getOrganizationFromDomain();

        console.log("[LogtoStreaming] Organization check:", {
          userOrgs,
          domainOrg,
          hostname: window.location.hostname,
          claims: {
            sub: claims?.sub,
            email: claims?.email,
            name: claims?.name,
          },
        });

        // If domain org is in user's orgs, use it
        if (domainOrg && userOrgs.includes(domainOrg)) {
          setCurrentOrganizationId(domainOrg);
        } else if (userOrgs.length > 0) {
          // Fallback to first org if domain org not available
          console.warn(
            `[LogtoStreaming] Domain org ${domainOrg} not in user orgs, using ${userOrgs[0]}`,
          );
          setCurrentOrganizationId(userOrgs[0]);
        } else {
          console.error("[LogtoStreaming] User has no organizations!");
          setCurrentOrganizationId(undefined);
        }

        // Sync user info to authStore — only if changed (prevents re-render cascade)
        if (claims) {
          const newName =
            claims.name ?? claims.username ?? claims.email?.split("@")[0];
          const newEmail = claims.email ?? undefined;
          const current = useAuthStore.getState().userInfo;

          if (
            current?.sub === claims.sub &&
            current?.email === newEmail &&
            current?.name === newName
          ) {
            console.log(
              "[LogtoStreaming] userInfo unchanged, skipping setState",
            );
          } else {
            useAuthStore.setState({
              isAuthenticated: true,
              isLoading: false,
              userInfo: {
                sub: claims.sub,
                email: newEmail,
                name: newName,
              },
            });
            console.log("[LogtoStreaming] Synced user info to authStore:", {
              sub: claims.sub,
              email: claims.email,
              name: claims.name,
            });
          }
        }
      } catch (err) {
        console.error("[LogtoStreaming] Failed to get ID token claims:", err);
      }
    };

    verifyOrganizationAndSyncUser();
  }, [isAuthenticated]);

  // Reset module-level guard on signout so next login re-verifies
  useEffect(() => {
    if (!isAuthenticated) _orgVerified = false;
  }, [isAuthenticated]);

  /**
   * Get access token — uses logtoRef to avoid depending on unstable hook refs.
   * Only currentOrganizationId is a real dependency (changes once after org verify).
   */
  const getAccessToken = useCallback(
    async (resource?: string): Promise<string | undefined> => {
      const currentLogto = logtoRef.current;
      if (!currentLogto.isAuthenticated) {
        console.warn("[LogtoStreaming] Not authenticated");
        return undefined;
      }

      // Get API resource from env or use provided resource
      const apiResource =
        resource || import.meta.env.VITE_LOGTO_RESOURCE || undefined;

      try {
        if (currentOrganizationId && apiResource) {
          const token = await currentLogto.getAccessToken(
            apiResource,
            currentOrganizationId,
          );
          if (token) {
            return token;
          }
          console.warn(
            "[LogtoStreaming] Organization-scoped token failed, trying without org",
          );
        }

        // Fallback: standard access token (may not have organization_id)
        const token = await currentLogto.getAccessToken(apiResource);
        return token ?? undefined;
      } catch (err) {
        console.error("[LogtoStreaming] Failed to get token:", err);
        // Detect expired refresh token (invalid_grant) → auto-signOut
        const errMsg = err instanceof Error ? err.message : String(err);
        if (
          errMsg.includes("invalid_grant") ||
          errMsg.includes("Grant request is invalid")
        ) {
          console.warn("[LogtoStreaming] Refresh token expired — signing out");
          try {
            currentLogto.signOut(window.location.origin);
          } catch {
            /* best-effort */
          }
        }
        return undefined;
      }
    },
    [currentOrganizationId],
  );

  // Expose token getter to non-React code (e.g. adminRequest, consentApi)
  // Set SYNCHRONOUSLY during render so it's available before children's effects
  // (useEffect runs bottom-up: children first, then parent — too late for consent check)
  // Uses generation counter so stale cleanup from old route's provider
  // does not wipe the freshly-set provider during route transitions.
  const tokenGeneration = setLogtoTokenProvider(getAccessToken);

  useEffect(() => {
    return () => clearLogtoTokenProvider(tokenGeneration);
  }, [tokenGeneration]);

  // Stable signOut wrapper that reads from ref
  const signOut = useCallback(
    async (redirectUri?: string) => logtoRef.current.signOut(redirectUri),
    [],
  );

  const value = useMemo(
    () => ({
      getAccessToken,
      signOut,
      isAuthenticated,
      currentOrganizationId,
    }),
    [getAccessToken, signOut, isAuthenticated, currentOrganizationId],
  );

  return (
    <LogtoStreamingContext.Provider value={value}>
      <RenderFence>{children}</RenderFence>
    </LogtoStreamingContext.Provider>
  );
}

/**
 * Blocks re-render cascade from LogtoProvider's isLoading toggles.
 * Every getAccessToken() call toggles isLoading in @logto/react v3,
 * which re-renders this provider. Without RenderFence, that cascade
 * propagates to Suspense → Outlet → page components, causing remounts.
 * Route changes still work because Outlet consumes React Router's context
 * directly (context updates bypass React.memo).
 */
const RenderFence = memo(
  ({ children }: { children: ReactNode }) => <>{children}</>,
  () => true,
);
RenderFence.displayName = "RenderFence";

/**
 * Hook to consume the context
 */
export function useLogtoStreaming(): LogtoStreamingContextType {
  return useContext(LogtoStreamingContext);
}

export default LogtoStreamingProvider;
